import { ethers } from "ethers";
import type {
  SyncChainCursor,
  SyncChainPage,
} from "../../joinsplit-v2/syncIndex";

const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** One page of the public chain feed through the data proxy. The response is
 * validated structurally here; ordering and cursor monotonicity are checked
 * by fetchChainDelta. */
export async function fetchChainPage(params: {
  baseUrl: string;
  headers: Record<string, string>;
  cursor: SyncChainCursor;
  expectedGeneration: number;
  limit?: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<SyncChainPage> {
  const limit = params.limit ?? 500;
  if (!finite(limit) || limit < 1 || limit > 500)
    throw new Error("Invalid chain feed page limit");
  if (!finite(params.expectedGeneration))
    throw new Error("Invalid chain index generation");
  const query = new URLSearchParams({
    tree: String(params.cursor.tree),
    leaf: String(params.cursor.leaf),
    block: String(params.cursor.block),
    log: String(params.cursor.log),
    limit: String(limit),
  });
  const response = await (params.fetchImpl ?? fetch)(
    `${params.baseUrl}/api/v1/sync/chain?${query.toString()}`,
    { headers: params.headers, signal: params.signal },
  );
  if (!response.ok)
    throw new Error(`Chain feed unavailable (${response.status})`);
  const body = (await response.json()) as Partial<SyncChainPage> & {
    success?: unknown;
    chain?: { ready?: unknown; generation?: unknown };
  };
  if (
    body?.chain?.ready !== true ||
    body.chain.generation !== params.expectedGeneration
  )
    throw new Error("Chain index changed or became unverified during sync");
  if (
    body?.success !== true ||
    !Array.isArray(body.leaves) ||
    !Array.isArray(body.nullifiers) ||
    body.leaves.length > limit ||
    // The cursor is an event location. A page includes the entire final event
    // even when its input array straddles the requested row limit (max 32).
    body.nullifiers.length > limit + 31 ||
    !body.next ||
    !body.more ||
    typeof body.more.leaves !== "boolean" ||
    typeof body.more.nullifiers !== "boolean"
  )
    throw new Error("Malformed chain feed page");
  const boundary = body.nullifiers[limit - 1];
  if (
    body.nullifiers
      .slice(limit)
      .some(
        (entry) =>
          entry.blockNumber !== boundary.blockNumber ||
          entry.logIndex !== boundary.logIndex,
      )
  )
    throw new Error("Malformed chain feed event boundary");
  return {
    leaves: body.leaves.map((leaf) => ({
      commitment: String(leaf.commitment).toLowerCase(),
      treeIndex: leaf.treeIndex,
      leafIndex: leaf.leafIndex,
    })),
    nullifiers: body.nullifiers.map((entry) => ({
      nullifier: String(entry.nullifier).toLowerCase(),
      blockNumber: entry.blockNumber,
      logIndex: entry.logIndex,
    })),
    next: body.next,
    more: body.more,
  };
}

/** Owner mailbox lane entries from a position onwards, paged like the
 * protected export. Returns the raw encrypted deliveries for decryption. */
export async function fetchInboxLane(params: {
  baseUrl: string;
  recipientKey: string;
  headers: Record<string, string>;
  lane: "pending" | "protected";
  cursor: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<Record<string, unknown>[]> {
  if (!finite(params.cursor)) throw new Error("Invalid inbox lane cursor");
  const notes: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  let cursor = String(params.cursor);
  for (let page = 0; ; page++) {
    if (page >= 100) throw new Error("Inbox lane pagination exceeded limit");
    if (seen.has(cursor)) throw new Error("Invalid inbox lane pagination");
    seen.add(cursor);
    const response = await (params.fetchImpl ?? fetch)(
      `${params.baseUrl}/api/v1/notes/${params.lane}/${params.recipientKey}?cursor=${encodeURIComponent(cursor)}`,
      { headers: params.headers, signal: params.signal },
    );
    if (!response.ok)
      throw new Error(`Inbox lane unavailable (${response.status})`);
    const result = await response.json();
    if (
      result?.success !== true ||
      !Array.isArray(result.notes) ||
      result.notes.length > 100 ||
      result.notes.some(
        (note: unknown) =>
          !note || typeof note !== "object" || Array.isArray(note),
      ) ||
      (result.nextCursor != null &&
        (typeof result.nextCursor !== "string" ||
          !/^\d+$/.test(result.nextCursor)))
    )
      throw new Error("Malformed inbox lane page");
    notes.push(...result.notes);
    if (!result.nextCursor) break;
    cursor = result.nextCursor;
  }
  return notes;
}

// The pool's own signature (the full validation reads it the same way in
// joinsplit-v2/sync.ts); any other parameter types select a function the pool
// does not have and revert without data.
const TOKEN_HASH_ABI = new ethers.Interface([
  "function computeTokenHashForTree(uint256 treeIndex, address token, uint8 tokenType, uint256 tokenSubID) view returns (bytes32)",
]);

/** Pool token hashes are deterministic per (pool, tree, token); after the first
 * read they come from local storage, so incremental syncs need no chain read. */
export function createTokenHashReader(params: {
  chainId: number;
  provider: () => ethers.Provider;
  storage?: Pick<Storage, "getItem" | "setItem"> | null;
}): (pool: string, treeIndex: number, tokenAddress: string) => Promise<string> {
  const memory = new Map<string, Promise<string>>();
  const storage =
    params.storage === undefined
      ? typeof window === "undefined"
        ? null
        : window.localStorage
      : params.storage;
  return (pool, treeIndex, tokenAddress) => {
    const token = ethers.getAddress(tokenAddress);
    const key = `stealthos_token_hash_v1:${params.chainId}:${pool.toLowerCase()}:${treeIndex}:${token.toLowerCase()}`;
    let pending = memory.get(key);
    if (!pending) {
      pending = (async () => {
        try {
          const cached = storage?.getItem(key);
          if (cached && /^0x[0-9a-f]{64}$/.test(cached)) return cached;
        } catch {
          /* storage unavailable */
        }
        const contract = new ethers.Contract(
          pool,
          TOKEN_HASH_ABI,
          params.provider(),
        );
        const value = String(
          await contract.computeTokenHashForTree(treeIndex, token, 0, 0),
        ).toLowerCase();
        if (!/^0x[0-9a-f]{64}$/.test(value))
          throw new Error("Pool returned an invalid token hash");
        try {
          storage?.setItem(key, value);
        } catch {
          /* storage unavailable */
        }
        return value;
      })();
      memory.set(key, pending);
      pending.catch(() => memory.delete(key));
    }
    return pending;
  };
}
